/**
 * useRecommendations hook.
 *
 * Calls our /api/recommander/:source/:deckId endpoint (which scrapes the SSR'd
 * Astro recommend page) to get the rich recommendation payload — both the
 * flat top-picks list and per-bucket function categories (Ramp, SpotRemoval,
 * MassRemoval, CardAdvantage, Tutors, UtilityLands, Staples). Results are
 * cached by moxfieldDeckId so editing the deck doesn't re-fetch.
 */

import { useState, useEffect } from 'react';
import type { RecommanderCard, RecommanderResponse } from '@mtg-deck-tool/shared';
import { useDeckStore } from '../stores/deck-store.js';
import { api } from '../lib/api-client.js';

export type RecommanderData = RecommanderResponse['data'];

export interface UseRecommendationsResult {
  data: RecommanderData | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Walk a `data` payload and attach `resolvedCard` to every RecommanderCard whose
 * name we recognise locally. The same oracle_id appears in `recommendations` and
 * many typed buckets, so we resolve once and back-fill in place.
 */
async function hydrateResolvedCards(data: RecommanderData): Promise<void> {
  const allCards: RecommanderCard[] = [
    ...data.recommendations,
    ...data.includes,
    ...data.key_cards,
    ...Object.values(data.typed_recommendations).flat(),
  ];
  const uniqueNames = Array.from(new Set(allCards.map((c) => c.name)));
  if (uniqueNames.length === 0) return;
  try {
    const resolved = await api.resolveCards(uniqueNames);
    const byName = new Map(resolved.found.map((c) => [c.name, c]));
    for (const rec of allCards) {
      const full = byName.get(rec.name);
      if (full) rec.resolvedCard = full;
    }
  } catch {
    // Non-fatal — cards just lack hydration; UI falls back to scryfall image lookups.
  }
}

export function useRecommendations(): UseRecommendationsResult {
  const moxfieldDeckId = useDeckStore((s) => s.moxfieldDeckId);
  const recommendationsCache = useDeckStore((s) => s.recommendationsCache);
  const setRecommendationsCache = useDeckStore((s) => s.setRecommendationsCache);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!moxfieldDeckId) {
      setError(null);
      return;
    }
    if (recommendationsCache.has(moxfieldDeckId)) {
      setError(null);
      return;
    }

    let cancelled = false;
    const deckId = moxfieldDeckId;

    setIsLoading(true);
    setError(null);

    api
      .getRecommendations('moxfield', deckId)
      .then(async (resp) => {
        if (cancelled) return;
        await hydrateResolvedCards(resp.data);
        if (!cancelled) setRecommendationsCache(deckId, resp.data);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not load recommendations. Try again later.');
        // Cache an empty payload so isImportLoading clears even on failure.
        setRecommendationsCache(deckId, {
          commander_deck_count: 0,
          ci: '',
          recommendations: [],
          typed_recommendations: {},
          includes: [],
          key_cards: [],
          staple_density: 0,
        } satisfies RecommanderData);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moxfieldDeckId]);

  const data = moxfieldDeckId
    ? ((recommendationsCache.get(moxfieldDeckId) as RecommanderData | undefined) ?? null)
    : null;

  return { data, isLoading, error };
}
